import { toast } from 'react-hot-toast';

// Import types
import type { SaveRange } from '../../types/date';

// Import utils
import {
  formatTime,
  formatColumnDate,
  hmToViewerAtYmd,
} from '../../utils/date';

export type EventHeaderProps = {
  eventTitle: string;
  isOwner: boolean;
  ownerTimezone?: string;
  timezone: string;
  hourMode: '12h' | '24h' | string;
  gridStart: string; // HH:MM in owner/base tz
  gridEnd: string; // HH:MM in owner/base tz
  savedRange?: SaveRange;
  actualYmds: string[]; // fallback when no saved range
  openPopup: () => void;
};

const EventHeader = (props: EventHeaderProps) => {
  const {
    eventTitle,
    isOwner,
    ownerTimezone,
    timezone,
    hourMode,
    gridStart,
    gridEnd,
    savedRange,
    actualYmds,
    openPopup,
  } = props;

  const baseTz = ownerTimezone || timezone;
  const refYmd = savedRange?.fromYmd || actualYmds[0];

  const startLabel = formatTime(
    hmToViewerAtYmd(gridStart, refYmd, baseTz, timezone),
    hourMode,
  );
  const endLabel = formatTime(
    hmToViewerAtYmd(gridEnd, refYmd, baseTz, timezone),
    hourMode,
  );

  return (
    <div className="flex-1 w-full">
      <div className="text-start text-balance text-xl sm:text-2xl font-semibold mb-2">
        {eventTitle || 'Event name'}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base font-semibold text-gray-600">
        {savedRange?.fromYmd && savedRange?.toYmd ? (
          <div className="flex flex-wrap items-center gap-1">
            <span>
              {formatColumnDate(savedRange.fromYmd, 'MMM D', baseTz, timezone)}{' '}
              –
            </span>
            <span>
              {formatColumnDate(savedRange.toYmd, 'MMM D', baseTz, timezone)}
            </span>
            <span>({startLabel} -</span>
            <span>{endLabel})</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            <span>
              {formatColumnDate(actualYmds[0], 'MMM D', baseTz, timezone)}
            </span>
            <span>
              ({startLabel} - {endLabel})
            </span>
          </div>
        )}
        <button
          onClick={function () {
            if (!isOwner) {
              toast.error('Only the owner can edit the event');
              return;
            }
            openPopup();
          }}
          disabled={!isOwner}
          className={
            'underline whitespace-nowrap ' +
            (isOwner
              ? 'text-green-700 hover:text-green-800'
              : 'text-gray-400 cursor-not-allowed')
          }
          title={isOwner ? 'Edit event' : 'Only the owner can edit the event'}>
          Edit Event
        </button>
      </div>
    </div>
  );
};

export default EventHeader;
